import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Save, Target } from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';

const SPECIALTIES = [
  'General', 'Cardiology', 'Diabetology', 'Ophthalmology', 'Gynecology',
  'Pediatrics', 'Orthopedics', 'ENT', 'Dermatology', 'Preventive Health', 'Other',
];

const EMPTY = {
  name: '', description: '', specialty_focus: '',
  start_datetime: '', end_datetime: '',
  location_name: '', expected_footfall: '', actual_footfall: '',
  budget: '', actual_spend: '', status: 'planned',
};

// Convert ISO datetime → local input format "YYYY-MM-DDTHH:MM"
const toLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function NewHealthCamp() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { create: apiCreate, update: apiUpdate, fetch: apiFetch } = useApiMethods({ throwOnFail: true });
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await apiFetch(`engage/health-camps/${id}/`);
        const d = res?.data || res?.data?.data;
        if (d) setForm({ ...EMPTY, ...d,
          start_datetime: toLocal(d.start_datetime),
          end_datetime: toLocal(d.end_datetime) });
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load camp');
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.start_datetime || saving) return;
    setSaving(true); setError(null);
    try {
      const payload = { ...form };
      if (!payload.end_datetime) delete payload.end_datetime;
      if (!payload.expected_footfall) delete payload.expected_footfall;
      if (!payload.actual_footfall && !isEdit) delete payload.actual_footfall;
      if (!payload.budget) delete payload.budget;
      if (!payload.actual_spend && !isEdit) delete payload.actual_spend;
      if (isEdit) {
        await apiUpdate(`/engage/health-camps/${id}/`, payload);
        navigate(`/engage/health-camps/${id}`);
      } else {
        const resp = await apiCreate('/engage/health-camps/', payload);
        const created = resp?.data || resp?.data?.data;
        navigate(created?.id ? `/engage/health-camps/${created.id}` : '/engage/health-camps');
      }
    } catch (err) {
      setError(err?.response?.data?.message || (err?.response?.data && JSON.stringify(err.response.data)) || 'Failed to save');
      setSaving(false);
    }
  };

  const backTo = isEdit ? `/engage/health-camps/${id}` : '/engage/health-camps';
  const INPUT = "w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-pink-500";
  const L = ({ children }) => <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{children}</label>;

  if (loading) return <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading…</div>;

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-4">
      <button onClick={() => navigate(backTo)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center text-white shadow-sm"><Target className="w-5 h-5" /></div>
        <div><h1 className="text-xl font-bold text-gray-900 dark:text-white">{isEdit ? 'Edit' : 'Plan'} Health Camp</h1></div>
      </div>
      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}
      <form onSubmit={submit} className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm space-y-4">
        <div><L>Camp name <span className="text-red-500">*</span></L><input required value={form.name} onChange={e => set('name', e.target.value)} className={INPUT} /></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <L>Specialty focus</L>
            <select value={form.specialty_focus} onChange={e => set('specialty_focus', e.target.value)} className={INPUT}>
              <option value="">— Select —</option>
              {SPECIALTIES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <L>Status</L>
            <select value={form.status} onChange={e => set('status', e.target.value)} className={INPUT}>
              <option value="planned">Planned</option>
              <option value="live">Live</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div><L>Start date/time <span className="text-red-500">*</span></L><input required type="datetime-local" value={form.start_datetime} onChange={e => set('start_datetime', e.target.value)} className={INPUT} /></div>
          <div><L>End date/time</L><input type="datetime-local" value={form.end_datetime} onChange={e => set('end_datetime', e.target.value)} className={INPUT} /></div>
          <div><L>Location</L><input value={form.location_name} onChange={e => set('location_name', e.target.value)} className={INPUT} /></div>
          <div><L>Expected footfall</L><input type="number" value={form.expected_footfall} onChange={e => set('expected_footfall', e.target.value)} className={INPUT} /></div>
          <div><L>Actual footfall</L><input type="number" value={form.actual_footfall} onChange={e => set('actual_footfall', e.target.value)} className={INPUT} /></div>
          <div><L>Budget (₹)</L><input type="number" value={form.budget} onChange={e => set('budget', e.target.value)} className={INPUT} /></div>
          <div><L>Actual spend (₹)</L><input type="number" value={form.actual_spend} onChange={e => set('actual_spend', e.target.value)} className={INPUT} /></div>
        </div>
        <div><L>Description</L><textarea rows={3} value={form.description} onChange={e => set('description', e.target.value)} className={INPUT} /></div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
          <button type="button" onClick={() => navigate(backTo)} className="px-4 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
          <button type="submit" disabled={saving || !form.name.trim() || !form.start_datetime} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-pink-600 text-white text-sm shadow-sm hover:bg-pink-700 disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Plan Camp')}
          </button>
        </div>
      </form>
    </div>
  );
}
